import { Link, Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import type { MouseEvent } from "react";
import { useAuth } from "@/auth";
import {
  AppShell,
  Avatar,
  Flex,
  Group,
  Menu,
  Text,
  type StackProps,
  Stack,
  Image,
  Center,
  ActionIcon,
  useMantineColorScheme,
  useMantineTheme,
  Box,
} from "@mantine/core";
import { useMe, useNotifications } from "@/api";
import {
  CaretDownIcon,
  GearIcon,
  LineSegmentsIcon,
  ListMagnifyingGlassIcon,
  ShieldPlusIcon,
  SquaresFourIcon,
  type IconProps,
  PowerIcon,
  SunIcon,
  MoonIcon,
  SidebarSimpleIcon,
  ListIcon,
} from "@phosphor-icons/react";
import { useState, createContext, useContext, useEffect, useRef } from "react";
import { useMediaQuery } from "@mantine/hooks";
import NotificationBell from "@/components/notification-bell";
import { notifications } from "@mantine/notifications";
import { checkProfileCompletion } from "@/pages/app/Home";

// Context for managing sidebar state
interface SidebarContextType {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};


// Helper function to get initials from user's name
const getInitials = (
  firstName?: string | null,
  lastName?: string | null
): string => {
  const first = firstName?.charAt(0).toUpperCase() || "";
  const last = lastName?.charAt(0).toUpperCase() || "";
  
  // Return first and last initial, or first initial only if no last name
  if (first && last) {
    return `${first}${last}`;
  } else if (first) {
    return first;
  } else if (last) {
    return last;
  }
  return "U"; // Default to "U" for User if no name available
};

export default function AppLayout() {
  const theme = useMantineTheme();
  const auth = useAuth();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const routerNavigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Default to closed, will be set by useEffect
  const [initialized, setInitialized] = useState(false);
  
  const { data: userData, isLoading: isLoadingUser } = useMe();
  const location = useLocation();
  // Notifications are polled by useNotifications (NOTIFICATIONS_POLL_MS); the old
  // backend's WebSocket feed is gone. New arrivals surface as a toast below.
  const { data: notificationsPage } = useNotifications({ page_size: 50 });
  const seenNotificationIds = useRef<Set<number> | null>(null);
  useEffect(() => {
    const rows = notificationsPage?.results ?? [];
    if (!seenNotificationIds.current) {
      seenNotificationIds.current = new Set(rows.map((n) => n.id));
      return;
    }
    for (const n of rows) {
      if (seenNotificationIds.current.has(n.id)) continue;
      seenNotificationIds.current.add(n.id);
      if (n.title?.trim() && n.message?.trim()) notifications.show({ title: n.title, message: n.message });
    }
  }, [notificationsPage]);
  useEffect(() => {
    if (!isLoadingUser && userData) {
      const currentIsHomePage = !!matchPath({ path: "/app/home", end: false }, location.pathname);
      
      if (!currentIsHomePage) {
        const { isComplete } = checkProfileCompletion(userData);
        
        if (!isComplete) {
          routerNavigate("/app/home");
        }
      }
    }
  }, [isLoadingUser, userData, location.pathname, routerNavigate]);

  // Set initial sidebar state based on screen size only on first load
  useEffect(() => {
    if (!initialized) {
      setSidebarCollapsed(isMobile);
      setInitialized(true);
    }
  }, [isMobile, initialized]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const HEADER_HEIGHT = 56;
  const TABS: TabDesc[] = [
    {
      Icon: SquaresFourIcon,
      label: "Home",
      value: "home",
      route: "/app/home",
    },
    {
      Icon: LineSegmentsIcon,
      label: "OneSelf Path®",
      value: "path",
      route: "/app/path",
    },
    {
      Icon: ListMagnifyingGlassIcon,
      label: "PathFinder",
      value: "path-finder",
      route: "/app/path-finder",
    },
    {
      Icon: ShieldPlusIcon,
      label: "Engagement Center",
      value: "engagement-center",
      route: "/app/engagement-center",
    },
    {
      Icon: GearIcon,
      label: "Settings",
      value: "settings",
      route: "/app/settings",
    },
  ];
  const activeTabObj = TABS.find((it) =>
    matchPath({ path: it.route, end: false }, location.pathname)
  );
  const activeTabLabel = activeTabObj?.label ?? "";

  interface TabDesc {
    Icon: React.FC<IconProps>;
    label: string;
    value: string;
    route: string;
  }

  const StackedLink = ({
    to,
    onClick,
    children,
    ...props
  }: Omit<StackProps, "onClick"> & { to: string; onClick?: (e: MouseEvent<HTMLAnchorElement>) => void }) => (
    <Link to={to} onClick={onClick} style={{ textDecoration: "none", color: "inherit" }}>
      <Stack styles={{ root: { cursor: "pointer" } }} {...props}>
        {children}
      </Stack>
    </Link>
  );
  
  const contextValue: SidebarContextType = {
    sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
  };

  // Mobile Header Component
  const MobileHeader = () => (
    <Box
      bg={colorScheme === "dark" ? theme.colors.dark[8] : theme.white}
      style={{
        borderBottom: `1px solid ${colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[2]}`,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Main Header */}
      <Flex justify="space-between" align="center" p="md" pos="relative">
        <Group gap="md">
          <ActionIcon variant="subtle" size="lg" onClick={toggleSidebar}>
            <ListIcon size={24} />
          </ActionIcon>
        </Group>

        {/* Centered Logo */}
        <Box
          pos="absolute"
          left="50%"
          top="50%"
          style={{
            transform: "translate(-50%, -50%)",
          }}
        >
          <Image
            src="/assets/in_progress.png"
            alt="OneSelf Logo"
            h={40}
            w={40}
            fit="contain"
          />
        </Box>

        <Group gap="md" align="center">
          {/* Theme Toggle Button */}
          <ActionIcon
            variant="outline"
            color={
              colorScheme === "dark"
                ? theme.colors.yellow[3]
                : theme.colors.blue[6]
            }
            title="Toggle color scheme"
            onClick={() => toggleColorScheme()}
          >
            {colorScheme === "dark" ? (
              <SunIcon size={15} />
            ) : (
              <MoonIcon size={15} />
            )}
          </ActionIcon>

          {/* Notification Icon */}
          <NotificationBell size={24} variant="subtle" />

          {/* User Profile Menu */}
          <Menu>
            <Menu.Target>
              <Avatar
                size="md"
                src={userData?.profile_picture && userData.profile_picture.trim() !== "" ? userData.profile_picture : undefined}
                color="blue"
                style={{ cursor: "pointer" }}
              >
                {(!userData?.profile_picture || userData.profile_picture.trim() === "") && getInitials(
                  userData?.first_name,
                  userData?.last_name
                )}
              </Avatar>
            </Menu.Target>
            <Menu.Dropdown w={200}>
              <Menu.Item
                onClick={() => {
                  auth.signOut().finally(() => {
                    setTimeout(() => {
                      window.location.href = "/auth/login";
                    }, 0);
                  });
                }}
              >
                <Center>
                  <PowerIcon size={16} style={{ marginRight: 8 }} />
                  Log out
                </Center>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Flex>
    </Box>
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <AppShell
      layout="alt"
      header={{ height: { base: 0, sm: HEADER_HEIGHT } }}
      navbar={{
        width: sidebarCollapsed ? 70 : 250,
        breakpoint: "sm",
        collapsed: { mobile: sidebarCollapsed },
      }}
      style={{
        backgroundColor: colorScheme === "dark" ? "#000000" : "transparent",
      }}
    >
      <AppShell.Header>
        {/* <Group h="100%" px="md">
          <Burger
            opened={mobileOpened}
            onClick={toggleMobile}
            hiddenFrom="sm"
            size="sm"
          />
          <Burger
            opened={desktopOpened}
            onClick={toggleDesktop}
            visibleFrom="sm"
            size="sm"
          />
          Logo1
        </Group> */}

        <Flex h={"100%"} p={"xs"} gap={"md"} visibleFrom="sm">
          <Flex justify="flex-start" w={"100%"}>
            {activeTabLabel && (
              <Text fw={600} size="lg" c={ colorScheme === "dark" ? theme.colors.neutral[0] :   theme.colors.neutral[7]}>
                {activeTabLabel}
              </Text>
            )}
          </Flex>

          <Flex  justify="flex-end" style={{ width: "100%" }}>
            <Group gap={40}>
              <div style={{ position: "relative", display: "inline-block" }}>
                <ActionIcon
                  variant="outline"
                  color={colorScheme === "dark" ? theme.colors.yellow[3] : theme.colors.blue[6]}
                  title="Toggle color scheme"
                  mr={"xl"}
                  onClick={() => toggleColorScheme()}
                >
                  {colorScheme === "dark" ? (
                    <SunIcon size={35} />
                  ) : (
                    <MoonIcon size={35} />
                  )}
                </ActionIcon>

                <NotificationBell 
                  size={25} 
                  variant="subtle" 
                  color={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[7]}
                />
              </div>

              {/* User Profile Dropdown */}
              <Menu>
                <Menu.Target>
                  <Group mr={"md"} gap={"md"} style={{ cursor: "pointer" }}>
                    <Avatar
                      size={35}
                      radius="xl"
                      src={userData?.profile_picture && userData.profile_picture.trim() !== "" ? userData.profile_picture : undefined}
                      color="blue"
                    >
                      {(!userData?.profile_picture || userData.profile_picture.trim() === "") && getInitials(
                        userData?.first_name,
                        userData?.last_name
                      )}
                    </Avatar>

                    <Text size="md" c={colorScheme ==="dark" ? theme.colors.neutral[0] : theme.colors.neutral[8]}>
                      {userData?.first_name ?? ""}{" "}
                      {userData?.last_name ?? ""}
                    </Text>

                    <CaretDownIcon color={theme.colors.neutral[5]} size={25} />
                  </Group>
                </Menu.Target>
                <Menu.Dropdown w={250}>
                  <Menu.Item
                    onClick={() => {
                      auth.signOut().finally(() => {
                        setTimeout(() => {
                          navigate("/auth/login");
                        }, 0);
                      });
                    }}
                  >
                    <Center>
                      <PowerIcon size={20} style={{ marginRight: 8 }} />
                      Log out
                    </Center>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Flex>
        </Flex>
      </AppShell.Header>

            <AppShell.Navbar
        p={sidebarCollapsed ? "xs" : "md"}
        style={{
          width: sidebarCollapsed ? 70 : 250,
          backgroundColor: colorScheme === "dark" ? theme.colors.dark[8] : "white",
          borderRight: `1px solid ${colorScheme === "dark" ? theme.colors.dark[6] : "#EBEFF2"}`,
          boxShadow: "6px 0px 18px 0px rgba(0, 0, 0, 0.06)",
          transition: "width 0.3s ease",
        }}
      >
        <Stack justify="space-between" h="100%" gap={0}>
          {/* Logo Section */}
          <Box pt="md">
            {!sidebarCollapsed && (
              <Image
                src={
                  colorScheme === "dark"
                    ? "/assets/logo_White.png"
                    : "/assets/logo_main.png"
                }
                alt="OneSelf Logo"
                h={50}
                fit="contain"
                mb="xl"
              />
            )}
            {sidebarCollapsed && (
              <Box ta="center" mb="xl">
                <Image
                  src="/assets/in_progress.png"
                  alt="OneSelf Logo"
                  sizes="xs"
                  fit="contain"
                />
              </Box>
            )}
          </Box>

          {/* Navigation Items - Centered */}
          <Stack gap="xs" style={{ flex: 1 }}>
            {TABS?.map((tab) => {
              const { isComplete: isProfileComplete } = checkProfileCompletion(userData);
              const isHomeTab = tab.value === "home";
              const canNavigate = isHomeTab || isProfileComplete;
              
              return (
              <StackedLink 
                to={canNavigate ? tab.route : "/app/home"} 
                key={tab.value}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  if (!canNavigate) {
                    e.preventDefault();
                    routerNavigate("/app/home");
                    return;
                  }
                  
                  if (activeTabObj?.value !== tab.value && isMobile) {
                    setSidebarCollapsed(true);
                  }
                }}
              >
                <Flex
                  align="center"
                  justify={sidebarCollapsed ? "center" : "flex-start"}
                  gap={sidebarCollapsed ? 0 : "md"}
                  p="sm"
                  style={{
                    cursor: canNavigate ? "pointer" : "not-allowed",
                    borderRadius: "8px",
                    background:
                      activeTabObj?.value === tab.value
                        ? `linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)`
                        : "transparent",
                    minHeight: 40,
                    transition: "background-color 0.2s ease",
                    opacity: canNavigate ? 1 : 0.5,
                  }}
                  title={sidebarCollapsed ? tab.label : undefined}
                  onMouseEnter={(e) => {
                    if (activeTabObj?.value !== tab.value) {
                      e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                        ? "#555555"
                        : "#E0EDFE";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTabObj?.value !== tab.value) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <tab.Icon
                    size={20}
                    color={
                      activeTabObj?.value === tab.value
                        ? theme.colors.neutral[1] || "white"
                        : colorScheme === "dark"
                          ? theme.colors.neutral[0] || "white"
                          : theme.colors.neutral[7] || theme.colors.gray[6]
                    }
                  />
                  {!sidebarCollapsed && (
                    <Text
                      size="sm"
                      fw={activeTabObj?.value === tab.value ? 700 : 500}
                      c={
                        activeTabObj?.value === tab.value
                          ? theme.colors.neutral[1] || "white"
                          : colorScheme === "dark"
                            ? theme.colors.neutral[0] || "white"
                            : theme.colors.neutral[7] || theme.colors.gray[6]
                      }
                    >
                      {tab?.label}
                    </Text>
                  )}
                </Flex>
              </StackedLink>
              );
            })}
          </Stack>

          {/* Toggle Sidebar - Bottom */}
          <Box pb="md">
            <Group
              gap="xs"
              p="sm"
              justify={sidebarCollapsed ? "center" : "flex-start"}
              style={{ cursor: "pointer" }}
              onClick={toggleSidebar}
            >
              <SidebarSimpleIcon size={20} weight="duotone" />
              {!sidebarCollapsed && (
                <Text size="xs" c="#90A0B7">
                  Toggle sidebar
                </Text>
              )}
            </Group>
          </Box>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main
        style={{
          background: colorScheme === "dark" 
            ? "#000000" 
            : "linear-gradient(90deg, #FFF8F6 0%, #FAF8FB 50%, #F5F8FA 100%)",
          minHeight: "100vh",
        }}
      >
        {/* Mobile Header - shown on mobile only */}
        <Box hiddenFrom="sm">
          <MobileHeader />
        </Box>
        <Outlet />
      </AppShell.Main>
    </AppShell>
    </SidebarContext.Provider>
  );
}
